import React, { useMemo } from "react";

import { orderBy } from "lodash";

import useGetPortfolioPlanAssignmentRenderTree from "@fartherfinance/frontend/api/Investment/hooks/usePostPortfolioPlanAssignmentRenderTree";
import usePostPortfolioPlanRenderTree, {
  Entry,
} from "@fartherfinance/frontend/api/Investment/hooks/usePostPortfolioPlanRenderTree";
import {
  ClientId,
  PortfolioPlanAssignmentId,
  PortfolioPlanId,
} from "@fartherfinance/frontend/api/Types";

import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import useRequestAuth from "@src/multiCustodian/hooks/useRequestAuth";

import AssignmentsDropDown, { PortfolioPlanUnion } from "./AssignmentsDropDown";
import DrillInTable, {
  DrillInData,
  DrillInDataRow,
  DrillInRow,
} from "./DrillInTable";
import ErrorState from "./ErrorState";
import LoadingState from "./LoadingState";
import PlanTitle from "./PlanTitle";
import SecurityRow from "./SecurityRow";

interface Props {
  portfolioPlanId: PortfolioPlanId;
  portfolioPlanAssignment?: PortfolioPlanAssignmentId;
  resetView: () => void;
  onSelect: (el: PortfolioPlanUnion) => void;
  curItem?: string;
  clientId?: ClientId;
}

function createdNestedRow(entries: Entry[]): DrillInData[] {
  // in order to sort this by name as well we might have to make changes to this createdNestedRow function
  // https://linear.app/fartherfinance/issue/FIT-1243/sort-nested-securities-row-by-name-model-details-holdings-and
  return orderBy(entries, (entry) => entry.weight.toNumber(), "desc")
    .map((nestedPlan) => {
      switch (nestedPlan.type) {
        case "Security":
          return {
            type: "DataRow",
            row: (
              <SecurityRow
                securityId={nestedPlan.securityId}
                weight={nestedPlan.weight}
              />
            ),
            weight: nestedPlan.weight,
          } satisfies DrillInDataRow;

        case "StrategicAllocation":
          return null;

        case "NestedPlan":
          return {
            type: "DrillInRow",
            weight: nestedPlan.weight,
            leftColumn: <PlanTitle portfolioPlanId={nestedPlan.planId} />,
            rightColumn: nestedPlan.weight.toNumber().toLocaleString("en-US", {
              style: "percent",
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }),
            data: createdNestedRow(nestedPlan.entries),
            title: <PlanTitle portfolioPlanId={nestedPlan.planId} />,
          } satisfies DrillInRow;
      }
    })
    .filter((x) => x !== null);
}

const NestedPlanTable: React.FC<Props> = (props) => {
  const auth = useRequestAuth();
  const advisorAuth = useAdvisorRequestAuth();

  const nestedPlans = usePostPortfolioPlanRenderTree(
    props.portfolioPlanId,
    auth ?? advisorAuth
  );

  const nestedAssignmentPlans = useGetPortfolioPlanAssignmentRenderTree(
    props.portfolioPlanId,
    props.portfolioPlanAssignment ?? null,
    auth
  );

  // if we have a portfolioPlanAssignment, we should use the nestedAssignmentPlans
  const nestedPlansData = props.portfolioPlanAssignment
    ? nestedAssignmentPlans
    : nestedPlans;

  const data: DrillInData[] = useMemo(() => {
    if (nestedPlansData.data === undefined) {
      return [];
    }

    return createdNestedRow(nestedPlansData.data);
  }, [nestedPlansData.data]);

  if (nestedPlansData.isLoading) {
    return (
      <LoadingState
        leftTitle="Sleeves"
        rightTitle=""
        leftColumn="Portfolio"
        rightColumn="Weight"
      />
    );
  }

  if (nestedPlansData.hasError) {
    return (
      <ErrorState
        leftTitle="Sleeves"
        rightTitle=""
        errorMessage="Error loading table"
      />
    );
  }

  return (
    <DrillInTable
      leftTitle="Sleeves"
      rightTitle={
        props.clientId ? (
          <AssignmentsDropDown
            portfolioPlanId={props.portfolioPlanId}
            curItem={props.curItem}
            onSelect={props.onSelect}
          />
        ) : null
      }
      leftColumn="Portfolio"
      rightColumn="Weight"
      data={data}
      resetView={props.resetView}
    />
  );
};

export default NestedPlanTable;
